import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { DisplayModes } from 'services/webedi/types';
import { cn } from 'support/helpers/cn/cn';
import { EMPTY_FIELD } from 'support/helpers/const/const';

import { CellProps } from '../../types';
import { useCellClassNames, useDateConfigurations } from '../cellHelpers';
import { ViewWrapperCell } from './ViewWrapperCell';

export type Props<TData, TValue> = CellContext<TData, TValue> &
  Pick<CellProps, 'fieldId' | 'isHighlighted' | 'diffStatus' | 'originalValue' | 'wasAIEdited'>;
export const ViewDateCell = <TData = unknown, TValue = string>({
  row,
  cell,
  column,
  table,
  isHighlighted,
  fieldId,
  diffStatus,
  originalValue,
  wasAIEdited,
}: Props<TData, TValue>) => {
  const { t } = useTranslation();
  const {
    field: { value },
    fieldState: { error },
  } = useController({ name: fieldId });

  const {
    columnDef: { meta: columnMeta },
  } = column;

  const { formattedValue } = useDateConfigurations({
    value,
    mode: DisplayModes.view,
  });
  const { formattedValue: baseFormattedDate } = useDateConfigurations({
    value: originalValue ?? undefined,
    mode: DisplayModes.view,
  });

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });

  return (
    <ViewWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      cell={cell}
      diffStatus={diffStatus}
      originalValue={originalValue}
      formattedOriginalValue={baseFormattedDate}
      textAlign={columnMeta?.optionalConfig?.textAlign}
      wasAIEdited={wasAIEdited}
    >
      <span className="flex w-full min-w-0 items-center">
        <div
          className={cn('flex w-full min-w-0 gap-x-0.5 overflow-hidden tabular-nums', {
            'justify-start': columnMeta?.optionalConfig?.textAlign === 'left',
          })}
        >
          {!formattedValue && error?.type === 'required' ? (
            t('common:table.errors.requiredField')
          ) : formattedValue ? (
            <span className="flex-initial">{formattedValue}</span>
          ) : (
            <span className={cn({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
          )}
        </div>
      </span>
    </ViewWrapperCell>
  );
};
